import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, TextField, Select, MenuItem, FormControl, InputLabel, Divider,
  Alert, CircularProgress, Stack, Paper, IconButton, Tooltip,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  PersonOff as PersonOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';

const ACCESS_API = `${process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net'}/api/admin`;

const ROLES = ['super_admin', 'head_dietitian', 'dietitian', 'support'] as const;
type Role = typeof ROLES[number];

const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  head_dietitian: 'Head Dietitian',
  dietitian: 'Dietitian',
  support: 'Support',
};

const ROLE_COLORS: Record<Role, 'error' | 'warning' | 'info' | 'default'> = {
  super_admin: 'error',
  head_dietitian: 'warning',
  dietitian: 'info',
  support: 'default',
};

interface AdminEntry {
  id: string;
  email: string;
  role: Role;
  display_name: string;
  is_active: boolean;
  granted_at: string;
}

export function AdminAccessTab() {
  const [admins, setAdmins] = useState<AdminEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<Role>('dietitian');
  const [adding, setAdding] = useState(false);

  // Edit state — track which row is being edited
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<Role>('dietitian');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeader = { Authorization: adminApi.getAuthToken() };

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${ACCESS_API}/access`, { headers: authHeader });
      setAdmins(res.data.admins ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load admin list.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    setError(null);
    try {
      await axios.post(`${ACCESS_API}/access`, { email: newEmail.trim(), role: newRole, display_name: newName.trim() || newEmail.trim() }, { headers: authHeader });
      setSuccess(`${newEmail} added as ${ROLE_LABELS[newRole]}.`);
      setNewEmail(''); setNewName(''); setNewRole('dietitian'); setShowAddForm(false);
      await fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to add admin.');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (admin: AdminEntry) => {
    setEditingEmail(admin.email);
    setEditRole(admin.role);
    setEditName(admin.display_name);
  };

  const handleSaveEdit = async () => {
    if (!editingEmail) return;
    setSaving(true);
    setError(null);
    try {
      await axios.patch(`${ACCESS_API}/access/${encodeURIComponent(editingEmail)}`, { role: editRole, display_name: editName }, { headers: authHeader });
      setSuccess(`${editingEmail} updated.`);
      setEditingEmail(null);
      await fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (email: string) => {
    if (!window.confirm(`Deactivate ${email}? They will lose dashboard access immediately.`)) return;
    setError(null);
    try {
      await axios.delete(`${ACCESS_API}/access/${encodeURIComponent(email)}`, { headers: authHeader });
      setSuccess(`${email} deactivated.`);
      await fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to deactivate.');
    }
  };

  const handleReactivate = async (email: string) => {
    setError(null);
    try {
      await axios.patch(`${ACCESS_API}/access/${encodeURIComponent(email)}`, { is_active: true }, { headers: authHeader });
      setSuccess(`${email} reactivated.`);
      await fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to reactivate.');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>Admin Access</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAdmins} disabled={loading}
            sx={{ textTransform: 'none', color: '#FF8C00' }}>Refresh</Button>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setShowAddForm(v => !v)}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E07A00' } }}>
            Add User
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Manage who can access this dashboard and which tabs they see. Changes take effect on the user's next login.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Add user form */}
      {showAddForm && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: '#FF8C00' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>Add new admin user</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField label="Email" size="small" value={newEmail} onChange={e => setNewEmail(e.target.value)}
              placeholder="user@example.com" sx={{ minWidth: 220 }} />
            <TextField label="Display name" size="small" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="Full Name" sx={{ minWidth: 180 }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Role</InputLabel>
              <Select value={newRole} label="Role" onChange={e => setNewRole(e.target.value as Role)}>
                {ROLES.map(r => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} pt={0.5}>
              <Button variant="contained" onClick={handleAdd} disabled={adding || !newEmail.trim()}
                sx={{ borderRadius: '20px', textTransform: 'none', bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E07A00' } }}>
                {adding ? <CircularProgress size={16} color="inherit" /> : 'Add'}
              </Button>
              <Button variant="outlined" onClick={() => setShowAddForm(false)}
                sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#FF8C00', color: '#FF8C00' }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map(admin => (
              <TableRow key={admin.email} sx={{ opacity: admin.is_active ? 1 : 0.5 }}>
                <TableCell>
                  {editingEmail === admin.email ? (
                    <TextField size="small" value={editName} onChange={e => setEditName(e.target.value)} sx={{ width: 150 }} />
                  ) : admin.display_name}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{admin.email}</TableCell>
                <TableCell>
                  {editingEmail === admin.email ? (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select value={editRole} onChange={e => setEditRole(e.target.value as Role)}>
                        {ROLES.map(r => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip label={ROLE_LABELS[admin.role] ?? admin.role} color={ROLE_COLORS[admin.role] ?? 'default'} size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={admin.is_active ? 'Active' : 'Inactive'} size="small"
                    sx={{ bgcolor: admin.is_active ? '#E8F5E9' : '#F5F5F5', color: admin.is_active ? '#2E7D32' : '#757575', fontWeight: 600 }} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {editingEmail === admin.email ? (
                      <>
                        <Tooltip title="Save">
                          <IconButton size="small" onClick={handleSaveEdit} disabled={saving}>
                            <CheckIcon fontSize="small" sx={{ color: '#2E7D32' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={() => setEditingEmail(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit role / name">
                          <IconButton size="small" onClick={() => startEdit(admin)}>
                            <EditIcon fontSize="small" sx={{ color: '#FF8C00' }} />
                          </IconButton>
                        </Tooltip>
                        {admin.is_active ? (
                          <Tooltip title="Deactivate access">
                            <span>
                              <IconButton size="small" onClick={() => handleDeactivate(admin.email)}
                                disabled={admin.email === 'info@famelia.app'}>
                                <PersonOffIcon fontSize="small" sx={{ color: admin.email === 'info@famelia.app' ? '#ccc' : '#D32F2F' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reactivate access">
                            <IconButton size="small" onClick={() => handleReactivate(admin.email)}>
                              <CheckIcon fontSize="small" sx={{ color: '#2E7D32' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Role permissions reference */}
      <Divider sx={{ mt: 4, mb: 3 }} />
      <Typography variant="h6" fontWeight={700} mb={2}>Role Permissions</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        What each role can see. Assign roles to users above. Changes take effect on the user's next login.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Tab / Feature</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Super Admin</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Head Dietitian</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Dietitian</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Support</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            { tab: '📝 Patient Inquiries', super: true, head: true, diet: true, supp: true },
            { tab: '🪪 Insurance card photos (in Patient Inquiries)', super: true, head: true, diet: true, supp: false },
            { tab: '📊 Analytics', super: true, head: true, diet: false, supp: false },
            { tab: '🔍 Moderation', super: true, head: false, diet: false, supp: false },
            { tab: '🚩 User Reports', super: true, head: false, diet: false, supp: true },
            { tab: '🗑️ Account Deletions', super: true, head: false, diet: false, supp: false },
            { tab: '🔬 Compliance Review', super: true, head: true, diet: true, supp: false },
            { tab: '✉️ Newsletter', super: true, head: false, diet: false, supp: false },
            { tab: '🔑 Admin Access (this tab)', super: true, head: false, diet: false, supp: false },
          ].map(row => (
            <TableRow key={row.tab}>
              <TableCell>{row.tab}</TableCell>
              {(['super', 'head', 'diet', 'supp'] as const).map(role => (
                <TableCell key={role} align="center">
                  {(row as any)[role] ? (
                    <Box sx={{ color: '#2E7D32', fontWeight: 700, fontSize: '1rem' }}>✓</Box>
                  ) : (
                    <Box sx={{ color: '#D32F2F', fontSize: '0.85rem' }}>—</Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box mt={2} p={1.5} bgcolor="#F5F5F5" borderRadius={2}>
        <Typography variant="caption" color="text.secondary">
          To change what a role can access, contact a developer — role definitions require a code change. To change which role a <em>person</em> has, use the table above.
        </Typography>
      </Box>
    </Box>
  );
}
